import { useMemo, useState } from 'react'
import {
  addMonths,
  endOfMonth,
  monthGrid,
  startOfMonth,
  toDayNumber,
} from '../../shared/date'
import { buildDayMap } from '../../shared/predict'
import { LogSheet } from '../components/LogSheet'
import { Card } from '../components/ui'
import { useI18n } from '../i18n'
import { useReady } from '../store'

export function CalendarScreen() {
  const { t } = useI18n()
  const { snapshot, prediction } = useReady()
  const [anchor, setAnchor] = useState(startOfMonth(snapshot.today))
  const [selected, setSelected] = useState<string | null>(null)

  // 一周从哪天开始跟着语言走，表头和格子必须用同一个值，否则整体错一列
  const grid = useMemo(() => monthGrid(anchor, t.meta.weekStart), [anchor, t.meta.weekStart])
  const dayMap = useMemo(
    () => buildDayMap(prediction, snapshot.periods, grid[0]!, grid[grid.length - 1]!),
    [prediction, snapshot.periods, grid],
  )
  const intimacyDates = useMemo(
    () => new Set(snapshot.intimacy.map((i) => i.date)),
    [snapshot.intimacy],
  )
  const symptomDates = useMemo(
    () => new Set(snapshot.symptoms.filter((s) => s.symptoms.length > 0).map((s) => s.date)),
    [snapshot.symptoms],
  )

  const monthStart = startOfMonth(anchor)
  const monthEnd = endOfMonth(anchor)
  const year = Number(anchor.slice(0, 4))
  const month = Number(anchor.slice(5, 7))
  const isCurrentMonth = startOfMonth(snapshot.today) === monthStart

  return (
    <div className="px-4 pb-6 space-y-4">
      <header className="safe-top pb-1 flex items-center justify-between px-1">
        <h1 className="text-2xl font-semibold">{t.calendar.title(year, month)}</h1>
        <div className="flex items-center gap-1">
          <ArrowButton dir="prev" onClick={() => setAnchor(addMonths(anchor, -1))} />
          <ArrowButton dir="next" onClick={() => setAnchor(addMonths(anchor, 1))} />
        </div>
      </header>

      {!isCurrentMonth && (
        <button
          onClick={() => setAnchor(startOfMonth(snapshot.today))}
          className="text-sm text-accent px-1"
        >
          {t.calendar.backToToday}
        </button>
      )}

      <Card className="px-3 py-4">
        <div className="grid grid-cols-7 mb-1">
          {t.calendar.weekdays.map((w, i) => (
            <div key={i} className="text-center text-xs text-muted py-1">
              {w}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-y-1">
          {grid.map((date) => {
            const info = dayMap.get(date)
            const inMonth =
              toDayNumber(date) >= toDayNumber(monthStart) &&
              toDayNumber(date) <= toDayNumber(monthEnd)
            return (
              <DayCell
                key={date}
                date={date}
                inMonth={inMonth}
                flow={info?.flow ?? null}
                predicted={info?.predictedPeriod ?? false}
                fertile={info?.fertile ?? false}
                ovulation={info?.ovulation ?? false}
                isToday={info?.isToday ?? false}
                intimacy={intimacyDates.has(date)}
                symptom={symptomDates.has(date)}
                onClick={() => setSelected(date)}
              />
            )
          })}
        </div>
      </Card>

      <Card>
        <div className="grid grid-cols-2 gap-y-3 gap-x-2 text-xs">
          <Legend swatch={<span className="w-4 h-4 rounded-full bg-period" />}>
            {t.calendar.legendPeriod}
          </Legend>
          <Legend
            swatch={
              <span className="w-4 h-4 rounded-full border-[1.5px] border-dashed border-period" />
            }
          >
            {t.calendar.legendPredicted}
          </Legend>
          <Legend swatch={<span className="w-4 h-4 rounded-full bg-fertile-soft" />}>
            {t.calendar.legendFertile}
          </Legend>
          <Legend
            swatch={
              <span className="w-4 h-4 rounded-full bg-fertile-soft grid place-items-center">
                <span className="w-1.5 h-1.5 rounded-full bg-ovulation" />
              </span>
            }
          >
            {t.calendar.legendOvulation}
          </Legend>
          <Legend swatch={<span className="text-[13px] leading-none">♥</span>}>
            {t.calendar.legendIntimacy}
          </Legend>
        </div>
      </Card>

      <LogSheet
        date={selected ?? snapshot.today}
        open={selected !== null}
        onClose={() => setSelected(null)}
      />
    </div>
  )
}

/**
 * 一格 = 一天。用「实心 vs 虚线」区分已发生和预测中，
 * 这样一眼就知道哪些是真的记过、哪些只是推算。
 */
function DayCell({
  date,
  inMonth,
  flow,
  predicted,
  fertile,
  ovulation,
  isToday,
  intimacy,
  symptom,
  onClick,
}: {
  date: string
  inMonth: boolean
  flow: string | null
  predicted: boolean
  fertile: boolean
  ovulation: boolean
  isToday: boolean
  intimacy: boolean
  symptom: boolean
  onClick: () => void
}) {
  const day = Number(date.slice(8, 10))

  // 点滴出血画得比正常流量淡一些
  const bg = flow
    ? flow === 'spot'
      ? 'bg-period/40 text-white'
      : 'bg-period text-white'
    : fertile
      ? 'bg-fertile-soft'
      : ''
  const ring = predicted ? 'border-[1.5px] border-dashed border-period' : ''
  const todayRing = isToday ? 'ring-2 ring-offset-2 ring-ink ring-offset-card' : ''

  return (
    <button
      onClick={onClick}
      disabled={!inMonth}
      aria-label={date}
      className={`relative aspect-square w-full grid place-items-center ${
        inMonth ? '' : 'opacity-25 pointer-events-none'
      }`}
    >
      <span
        className={`w-9 h-9 rounded-full grid place-items-center text-[13px] tabular-nums transition ${bg} ${ring} ${todayRing}`}
      >
        {day}
      </span>
      {/* 排卵日在格子中心下方点一个深绿点 */}
      {ovulation && !flow && (
        <span className="absolute bottom-[3px] w-1.5 h-1.5 rounded-full bg-ovulation" />
      )}
      {intimacy && (
        <span className="absolute top-0 right-1 text-[9px] text-period leading-none">♥</span>
      )}
      {symptom && !ovulation && (
        <span className="absolute bottom-[4px] w-1 h-1 rounded-full bg-muted/60" />
      )}
    </button>
  )
}

function Legend({
  swatch,
  children,
}: {
  swatch: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-4 h-4 grid place-items-center shrink-0 text-period">{swatch}</span>
      <span className="text-muted">{children}</span>
    </div>
  )
}

function ArrowButton({ dir, onClick }: { dir: 'prev' | 'next'; onClick: () => void }) {
  const { t } = useI18n()
  return (
    <button
      onClick={onClick}
      aria-label={dir === 'prev' ? t.calendar.prevMonth : t.calendar.nextMonth}
      className="w-10 h-10 rounded-full bg-card border border-line/70 grid place-items-center active:scale-95 transition"
    >
      <svg width="8" height="13" viewBox="0 0 8 13" fill="none">
        <path
          d={dir === 'prev' ? 'M6.5 1L1.5 6.5L6.5 12' : 'M1.5 1L6.5 6.5L1.5 12'}
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  )
}
